import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useContext,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { pdfjsUrl } from '../../util/constants';
import { stem } from '../../util/stemmer';
import { initiate_interval_tree } from '../../util/interval_tree';
import { useCurrentlyReading } from '../../hooks/useCurrentlyReading';
import { catalogueDataFull } from '../Books/data';
import EndOfBookPopup from './EndOfBookPopup';
import AnnotationBox from '../../component/AnnotationBox';
import { useSelector } from 'react-redux';
import SignupLoginDialog from '../../component/SignupDialog';
import ReaderTutorial from './ReaderTutorial';
import { IframeRefContext } from '../../context/IframeRefContext';

const tutorialSteps = [
  {
    targetId: 'leaveButton',
    text: 'This button helps you close this book and go back to previous page!!',
  },
  {
    targetId: 'sidebarToggle', // Example ID for a search button
    text: 'Use this button to see the Table of Content for this Volume. Click again to close it. TRY NOW!!',
  },
  {
    targetId: 'pageNumber', // Example ID for the page number input
    text: 'Enter a page number here and press Enter to jump directly to that page.',
  },
  {
    targetId: 'zoomOut', // Example ID for the zoom out button
    text: 'Use these controls to zoom in or out for better readability or fullscreen for larger screen. TRY!',
  },
  {
    targetPosition: 'top-center',
    text: 'On a phone a 💻👉📱 button will make letters bigger or back to original. TRY!',
  },
  {
    targetId: 'viewReader',
    text: 'Use night-mode for late night readings. TRY!',
  },
  {
    targetId: 'shareButton',
    text: 'This will let you share selected text with friends and family. TRY?',
  },
  {
    targetId: 'bookMark',
    text: 'This button will help you highlight the selected text',
  },
  {
    targetId: 'voiceButton',
    text: 'Opens Voice Panel so you can listen to the book! Use fullscreen button above to stop your phone from falling asleep in the middle of reading. TRY!',
  },

  {
    targetPosition: 'middle-center',
    text: 'Select any difficult letter to see its meaning. TRY!',
  },
  {
    targetPosition: 'bottom-center',
    text: 'Use arrow keys or < > below to flip pages!',
  },
];

// --- Watermark Function ---
/**
 * Finds the first <canvas> element in the provided document (from an iframe)
 * and draws a diagonal, transparent text watermark across it.
 *
 * @param {string} text - The text to be used for the watermark.
 * @param {Document} contentDoc - The document object of the iframe.
 */
function addWatermark(text, contentDoc) {
  // Find the first canvas element on the page.
  if (!contentDoc) return;
  const canvas = contentDoc.querySelector('canvas');
  if (!canvas) {
    console.error('No canvas element found in the iframe.');
    return;
  }

  // Get the 2D rendering context.
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Could not get 2D context from canvas.');
    return;
  }

  // --- Watermark Style Configuration ---
  // The font size is calculated based on the canvas width. A smaller divisor
  // results in a larger font, making the watermark span more of the canvas.
  const fontSize = canvas.width / 7;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.fillStyle = 'rgba(255, 0, 0, 0.15)'; // Red color with 30% opacity
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // --- Transformation and Drawing Logic ---
  ctx.save();
  const centerX = canvas.width / 2;
  const centerY = canvas.height / 2;
  ctx.translate(centerX, centerY);
  const angle = -Math.atan2(canvas.height, canvas.width);
  ctx.rotate(angle);
  ctx.fillText(text, 0, 0);
  ctx.restore();
}

// **HIGHLIGHT**: `width` and `height` have been removed from this class.
// They will now be set dynamically with inline styles.
const animationStyles = `
  @keyframes turnPage {
    0% {
      transform: perspective(1500px) rotateY(0deg);
      opacity: 1;
    }
    100% {
      transform: perspective(1500px) rotateY(-90deg);
      opacity: 0;
    }
  }

  .page-turn-cover {
    background-size: cover;
    background-position: center;
    transform-origin: left;
    box-shadow: 0 6px 15px rgba(0,0,0,0.2);
  }

  .page-turn-cover.animate {
    animation: turnPage 3s ease-in 1s forwards;
  }
`;

const OpeningBookAnimation = ({ imageUrl }) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  // **HIGHLIGHT**: New state to store the natural dimensions of the image.
  const [imageDimensions, setImageDimensions] = useState({
    width: 0,
    height: 0,
  });

  useEffect(() => {
    if (!imageUrl) return;
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      // **HIGHLIGHT**: When the image loads, capture its natural dimensions.
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      setIsImageLoaded(true);
    };
  }, [imageUrl]);

  // **HIGHLIGHT**: Calculate the display dimensions while maintaining aspect ratio.
  // We'll constrain the height to a maximum of 455px (30% larger).
  const maxHeight = 455;
  let displayWidth = 325; // Default width (30% larger)
  let displayHeight = 455; // Default height (30% larger)

  if (imageDimensions.height > 0) {
    const aspectRatio = imageDimensions.width / imageDimensions.height;
    displayHeight = maxHeight;
    displayWidth = displayHeight * aspectRatio;
  }

  if (!imageUrl) {
    return <div>Loading Book...</div>;
  }

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10,
        background: 'transparent',
      }}
    >
      <style>{animationStyles}</style>

      <h3
        style={{
          color: 'white',
          textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          marginBottom: '20px',
        }}
      >
        Opening your book...
      </h3>

      <div
        className={`page-turn-cover ${isImageLoaded ? 'animate' : ''}`}
        // **HIGHLIGHT**: Dynamic dimensions are applied here via inline styles.
        style={{
          backgroundImage: `url(${imageUrl})`,
          width: `${displayWidth}px`,
          height: `${displayHeight}px`,
          // Hide the element until the image is loaded to prevent a flash of the default size
          visibility: isImageLoaded ? 'visible' : 'hidden',
        }}
      ></div>
    </div>
  );
};
const CustomDialog = ({ message, onClose }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'sans-serif',
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '25px',
          borderRadius: '10px',
          boxShadow: '0 5px 15px rgba(0, 0, 0, 0.2)',
          maxWidth: '90%',
          width: '450px',
          textAlign: 'center',
        }}
      >
        <div
          style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}
          dangerouslySetInnerHTML={{ __html: message }}
        />
        <button
          onClick={onClose}
          style={{
            padding: '10px 25px',
            border: 'none',
            borderRadius: '5px',
            backgroundColor: '#007bff',
            color: 'white',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
          }}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

const googleTag = () => {
  try {
    if (typeof window !== 'undefined') {
      window.gtag('event', 'view_item', {
        event_category: 'engagement',
        event_label: 'url',
        value: document.location.href,
      });
    }
  } catch (err) {
    console.error(err);
  }
};
const findBook = (lang, volume, page) => {
  const last = (arr) => arr[arr.length - 1];
  const startPageNo = (book) => Number(last(book.Path.split('/')));
  const volumeName = (book) => book.Path.split('/')[4];
  const langName = (book) => book.Path.split('/')[3];

  let filteredByVolume = catalogueDataFull.filter(
    (b) => volumeName(b) === volume && langName(b) === lang
  );
  return filteredByVolume.find((book) => {
    return page >= startPageNo(book) && page <= book.EndPage;
  });
};

export default function Book(props) {
  initiate_interval_tree();

  const { setCurrentPage } = useCurrentlyReading();
  const [allowPageTurn, setAllowPageTurn] = useState(true);
  const [isTrackReading, setIsTrackReading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showNewFeatureDialog, setShowNewFeatureDialog] = useState(true);
  const [showCustomDialog, setShowCustomDialog] = useState(false);
  const [customDialogMessage, setCustomDialogMessage] = useState('');
  const confirmationShown = useRef(false);
  const { iframeRef, setIsIframeReady } = useContext(IframeRefContext);
  const params = useParams();
  let [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [dict, setDict] = useState(null);
  const user = useSelector((state) => state.userReducer);
  const [isSignupPopupOpen, setIsSignupPopupOpen] = useState(false);
  const [isTutorialVisible, setIsTutorialVisible] = useState(false); // State to control tutorial visibility

  const host =
    'localhost' === window.location.hostname
      ? 'http://localhost:8888/web/viewer.html'
      : pdfjsUrl;
  const src = `${host}?file=${props.bookUrl}`;

  const bookData = findBook(
    params.language,
    params.bookName,
    Number(params.pageNo)
  );
  const coverImageBaseUrl = 'https://www.baws.in/assets/catalog/';
  const coverImageUrl = bookData
    ? `${coverImageBaseUrl}${bookData.Filename}`
    : null;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 4000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!user.isLoggedIn) {
        console.log(
          'React Parent: User is not logged in. Opening signup dialog.'
        );
        setIsSignupPopupOpen(true);
      }
    }, 60000);
    return () => clearTimeout(timer);
  }, [user.isLoggedIn]);

  // --- Tutorial Logic ---
  const handleIframeLoad = () => {
    const hasSeenTutorial = localStorage.getItem('hasSeenReaderTutorial');
    localStorage.setItem('hasSeenReaderTutorial', 'true');
    if (!hasSeenTutorial) {
      setTimeout(() => {
        setIsTutorialVisible(true);
      }, 500); // Delay to ensure iframe content is rendered
    }
  };

  const handleTutorialFinish = () => {
    setIsTutorialVisible(false);
    localStorage.setItem('hasSeenReaderTutorial', 'true');
  };
  // --- End Tutorial Logic ---
  useEffect(() => {
    const status = searchParams.get('status');

    if (status === 'reading') {
      const language = params.language;
      const bookName = params.bookName;
      const pageNo = Number(params.pageNo);
      const catalogueBook = findBook(language, bookName, pageNo);

      const currReads =
        JSON.parse(localStorage.getItem('currentlyReading')) || [];
      const book = currReads.find((e) => e.link === catalogueBook?.Path);

      if (book && book.pageNo !== pageNo && !confirmationShown.current) {
        confirmationShown.current = true;
        let trackPage = window.confirm(
          'You have enabled tracking pages on this book. Do you want to start tracking the current page?'
        );
        setIsTrackReading(trackPage);
      } else if (book && book.pageNo === pageNo) {
        setIsTrackReading(true);
      }
    }

    const handleMessage = (event) => {
      try {
        if (
          event.data &&
          event.data.includes &&
          event.data.includes('action')
        ) {
          let message = JSON.parse(event.data);

          if (!message) return;

          switch (message.action) {
            case 'pageRendered': {
              const { bookParent, language, bookName, pageNo } = message.data;
              if (
                language === 'HI' &&
                bookName === 'Volume_40' &&
                pageNo === 486
              ) {
                // Call the watermark function when the page changes
                setTimeout(() => {
                  if (iframeRef.current && iframeRef.current.contentDocument) {
                    addWatermark('Disputed', iframeRef.current.contentDocument);
                  }
                }, 5); // Delay to ensure canvas is rendered
                if (showNewFeatureDialog) {
                  setCustomDialogMessage(
                    'Content of this page is disputed.  You can go here to see the original(cited on page 465 here) <br> <a href="https://baws.in/books/baws/MR/prabuddha_bharat_oct1956/pdf/1" target="_blank" rel="noopener noreferrer">प्रबुद्ध भारतः अम्बेडकर बौद्ध दीक्षा विशेषांक, 27अक्तूबर, 1956</a>.'
                  );
                  setShowCustomDialog(true);
                  setShowNewFeatureDialog(false); // Only show it once per component lifecycle
                }
              }
              break;
            }
            case 'pageChanged': {
              const { bookParent, language, bookName, pageNo } = message.data;
              let to;
              const catalogueBook = findBook(language, bookName, pageNo);
              if (catalogueBook) {
                document.title =
                  catalogueBook.Label ||
                  'Dr Babasaheb Ambedkar Writings And Speeches Center';
              }

              if (isTrackReading) {
                setCurrentPage(catalogueBook, pageNo);
                if (catalogueBook && catalogueBook.EndPage === pageNo) {
                  setAllowPageTurn(false);
                  console.log('You have reached the end of the book');
                }
                to = `/books/${bookParent}/${language}/${bookName}/pdf/${pageNo}?status=reading`;
              } else {
                to = `/books/${bookParent}/${language}/${bookName}/pdf/${pageNo}`;
              }

              window.history.replaceState({}, '', to);
              googleTag();
              break;
            }

            case 'loadSearchPage': {
              window.history.length > 2 ? navigate(-1) : navigate('/');
              break;
            }

            default:
              console.log(`unhandled action: ${message.action}`, message);
              break;
          }
        }
      } catch (err) {
        console.error(err);
      }
      return;
    };

    window.addEventListener('message', handleMessage, false);
    return () => window.removeEventListener('message', handleMessage, false);
  }, [
    isTrackReading,
    params.pageNo,
    navigate,
    searchParams,
    setCurrentPage,
    setAllowPageTurn,
  ]);

  useEffect(() => {
    const loadDictionary = async () => {
      try {
        const module = await import('../../asset/en_dict.json');
        setDict(module.default);
      } catch (err) {
        console.error('Failed to load dictionary:', err);
      }
    };

    loadDictionary();
  }, []);

  useLayoutEffect(() => {
    const messageListener = (event) => {
      if (event.data && event.data.type) {
        const { type } = event.data;
        if (type === 'saveHighlight' && !user.isLoggedIn) {
          console.log(
            'React Parent: User is not logged in. Opening signup dialog.'
          );
          setIsSignupPopupOpen(true);
        }
      }
    };
    window.addEventListener('message', messageListener, false);
    return () => window.removeEventListener('message', messageListener, false);
  }, [user.isLoggedIn]);

  window.dictionary = function (term) {
    var stemmed = stem(term);
    if (dict) return dict[stemmed];
  };

  useEffect(() => {
    // This is the setup part of the effect.
    // We could set isIframeReady to true here after the iframe loads if we wanted.

    // 2. This is the cleanup function.
    // It will run reliably when the BookView component unmounts.
    return () => {
      console.log('BookView is unmounting. Setting isIframeReady to false.');
      setIsIframeReady(false);
    };
  }, [setIsIframeReady]); // Add setIsIframeReady as a dependency

  return (
    <main>
      {isTutorialVisible && (
        <ReaderTutorial
          iframeRef={iframeRef}
          steps={tutorialSteps}
          onFinish={handleTutorialFinish}
        />
      )}
      {user.isBetaTester && <AnnotationBox iframeRef={iframeRef} />}
      <div
        style={{
          height: '100%',
          width: '100%',
          textAlign: 'center',
          position: 'absolute',
          left: 0,
          top: 0,
        }}
        className="mobile_pdf_render"
      >
        {isLoading && <OpeningBookAnimation imageUrl={coverImageUrl} />}

        {src && (
          <iframe
            ref={iframeRef} // Attach the ref to the iframe
            src={src}
            title="Book Viewer"
            height="100%"
            width="100%"
            style={{ border: 'none' }}
            onLoad={handleIframeLoad}
          />
        )}
      </div>
      {!allowPageTurn && <EndOfBookPopup />}
      {isSignupPopupOpen && (
        <SignupLoginDialog
          showSignupPopup={isSignupPopupOpen}
          setIsSignupPopupOpen={setIsSignupPopupOpen}
        />
      )}
      {showCustomDialog && (
        <CustomDialog
          message={customDialogMessage}
          onClose={() => setShowCustomDialog(false)}
        />
      )}
    </main>
  );
}
